import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App.jsx'
import './styles/index.css'

import { TenantProvider } from './contexts/TenantContext.jsx'
import { ModuleSwitchProvider } from './contexts/ModuleSwitchContext.jsx'
import { LanguageProvider } from './shared/i18n/LanguageContext.jsx'
import RootErrorBoundary from './components/RootErrorBoundary.jsx'

// Auto-reload once when a freshly-deployed service worker takes control.
//
// The PWA registers with registerType:'autoUpdate', so after a deploy the new service worker
// installs, skipWaiting-activates, and claims this client in the background — but the page already
// running keeps its OLD in-memory JS until a reload. Without this, a user lands on the stale app
// (e.g. logs in, still sees the old version) and has to manually refresh. Reloading on
// `controllerchange` makes the new deploy take effect on its own.
//
// Guards: only arm this when the page is ALREADY controlled by a worker — a first-ever visit has no
// controller, and its initial clientsClaim would otherwise trigger a pointless reload. The
// `reloaded` flag makes it fire at most once per page load, so there is no reload loop (after the
// reload the new worker is already in control and no further controllerchange fires).
if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
  let reloaded = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return
    reloaded = true
    window.location.reload()
  })
}

// Configure React Query Client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      onError: (error) => {
        const msg = error?.response?.data?.detail || error?.message || 'An error occurred'
        console.error('[Mutation Error]', msg, error)
      },
    },
  },
})

// Create root element and render the app
const root = createRoot(document.getElementById('root'))

root.render(
  <React.StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          {/* INSIDE TenantProvider and OUTSIDE App: the tenant default is rung (d) of the language
              chain, and it is host-resolved pre-auth precisely so it can reach the login page — which
              renders inside <App/>. Deliberately NOT inside AuthProvider: see LanguageContext.jsx for
              why the account preference is read from storage rather than from auth state. */}
          <TenantProvider>
            {/* Which held modules (Capital, Capex, Invoicing) the server has switched on. Above App so the
                nav, the portal tiles, the tours and the route guards all read one answer, fetched once. */}
            <ModuleSwitchProvider>
              <LanguageProvider>
                <App />
              </LanguageProvider>
            </ModuleSwitchProvider>
          </TenantProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </RootErrorBoundary>
  </React.StrictMode>
)
